import { DraftStatus } from '../../../utils/draft';
import { createEndpoint, Type } from '../../models/europrisme';
import {
  DISABLE_DRAFT_ORDER_REJECTED,
  DISABLE_DRAFT_ORDER_REQUESTED,
  DISABLE_DRAFT_ORDER_RESOLVED,
  DRAFT_ORDER_SEND_CART_REJECTED,
  DRAFT_ORDER_SEND_CART_REQUESTED,
  DRAFT_ORDER_SEND_CART_RESOLVED,
  GET_DRAFT_ORDERS_REJECTED,
  GET_DRAFT_ORDERS_REQUESTED,
  GET_DRAFT_ORDERS_RESOLVED,
  GET_MULTIACCOUNT_DRAFT_ORDER_COUNTS_REJECTED,
  GET_MULTIACCOUNT_DRAFT_ORDER_COUNTS_REQUESTED,
  GET_MULTIACCOUNT_DRAFT_ORDER_COUNTS_RESOLVED,
  GET_MULTIACCOUNT_SINGLE_DRAFT_ORDER_COUNT_REJECTED,
  GET_MULTIACCOUNT_SINGLE_DRAFT_ORDER_COUNT_REQUESTED,
  GET_MULTIACCOUNT_SINGLE_DRAFT_ORDER_COUNT_RESOLVED,
  GET_SINGLE_DRAFT_ORDER_REJECTED,
  GET_SINGLE_DRAFT_ORDER_REQUESTED,
  GET_SINGLE_DRAFT_ORDER_RESOLVED,
  MANAGE_DRAFT_ORDER_REJECTED,
  MANAGE_DRAFT_ORDER_REQUESTED,
  MANAGE_DRAFT_ORDER_RESOLVED,
} from '../types';

// ============================================================================
// getDraftOrders
// ============================================================================

export const getDraftOrdersRequested = () => ({
  type: GET_DRAFT_ORDERS_REQUESTED,
});

export const getDraftOrdersResolved = data => ({
  type: GET_DRAFT_ORDERS_RESOLVED,
  payload: { data },
});

export const getDraftOrdersRejected = error => ({
  type: GET_DRAFT_ORDERS_REJECTED,
  payload: { error },
});

export const getDraftOrdersEndpoint = createEndpoint(
  Type.GET,
  'getDraftOrders',
  { sG5CustomerCode: '' },
  {
    request: getDraftOrdersRequested,
    resolve: getDraftOrdersResolved,
    reject: getDraftOrdersRejected,
  },
  data => {
    try {
      const { xmlst } = data;
      const { Success } = xmlst;

      return Success === 'TRUE';
    } catch (error) {
      return false;
    }
  },
);

export const getDraftOrders = () => async (dispatch, getState) => {
  const { users } = getState();
  const { currentUser } = users;
  const { customerID } = currentUser;

  return dispatch(getDraftOrdersEndpoint({ sG5CustomerCode: customerID }));
};

// ============================================================================
// getMultiAccountSingleDraftOrderCount
// ============================================================================

export const getMultiAccountSingleDraftOrderCountRequested = () => ({
  type: GET_MULTIACCOUNT_SINGLE_DRAFT_ORDER_COUNT_REQUESTED,
});

export const getMultiAccountSingleDraftOrderCountResolved = data => ({
  type: GET_MULTIACCOUNT_SINGLE_DRAFT_ORDER_COUNT_RESOLVED,
  payload: { data },
});

export const getMultiAccountSingleDraftOrderCountRejected = error => ({
  type: GET_MULTIACCOUNT_SINGLE_DRAFT_ORDER_COUNT_REJECTED,
  payload: { error },
});

export const getMultiAccountSingleDraftOrderCountEndpoint = createEndpoint(
  Type.NON_DISPATCH,
  'getDraftOrders',
  { sG5CustomerCode: '' },
  data => {
    try {
      const { xmlst } = data;
      const { Success } = xmlst;

      return Success === 'TRUE';
    } catch (error) {
      return false;
    }
  },
);

export const getMultiAccountSingleDraftOrderCount = customerID => async dispatch => {
  return dispatch(
    getMultiAccountSingleDraftOrderCountEndpoint({
      sG5CustomerCode: customerID,
    }),
  );
};

// ============================================================================
// getMultiAccountDraftOrderCounts
// ============================================================================

export const getMultiAccountDraftOrderCountsRequested = () => ({
  type: GET_MULTIACCOUNT_DRAFT_ORDER_COUNTS_REQUESTED,
});

export const getMultiAccountDraftOrderCountsResolved = data => ({
  type: GET_MULTIACCOUNT_DRAFT_ORDER_COUNTS_RESOLVED,
  payload: { data },
});

export const getMultiAccountDraftOrderCountsRejected = error => ({
  type: GET_MULTIACCOUNT_DRAFT_ORDER_COUNTS_REJECTED,
  payload: { error },
});

export const getMultiAccountDraftOrderCounts = () => async (
  dispatch,
  getState,
) => {
  const { users } = getState();
  const { currentUser } = users;

  if (currentUser && currentUser.isMultiAccount) {
    const availableAccounts = currentUser.availableMultiAccountUsers;

    const response = await Promise.allSettled(
      availableAccounts.map(({ sCustomerCode }) => {
        return dispatch(getMultiAccountSingleDraftOrderCount(sCustomerCode));
      }),
    );

    const counts = response.map(({ value }) => {
      const { sG5CustomerCode } = value.fullParams;

      const currentCustomerAccount = availableAccounts.find(
        ({ sCustomerCode }) => sCustomerCode === sG5CustomerCode,
      );

      const customerName = currentCustomerAccount?.sCustomerName;

      if (value.success) {
        let items = value.json?.xmlst?.Item || [];

        if (typeof items === 'object' && !Array.isArray(items)) {
          items = [items];
        }

        const count = items.filter(
          draft => Number.parseInt(draft.iStatus, 10) === DraftStatus.SUBMITTED,
        ).length;

        return {
          customerCode: sG5CustomerCode,
          accountName: customerName,
          count,
        };
      }

      return {
        customerCode: sG5CustomerCode,
        accountName: customerName,
        count: 0,
      };
    });

    return { success: true, counts };
  }

  return dispatch(getMultiAccountDraftOrderCountsResolved());
};

// ============================================================================
// getSingleDraftOrder
// ============================================================================

export const getSingleDraftOrderRequested = params => ({
  type: GET_SINGLE_DRAFT_ORDER_REQUESTED,
  payload: { params },
});

export const getSingleDraftOrderResolved = (data, params) => ({
  type: GET_SINGLE_DRAFT_ORDER_RESOLVED,
  payload: { data, params },
});

export const getSingleDraftOrderRejected = (error, params) => ({
  type: GET_SINGLE_DRAFT_ORDER_REJECTED,
  payload: { error, params },
});

export const getSingleDraftOrderEndpoint = createEndpoint(
  Type.GET,
  'getDraftOrder',
  {
    sG5CustomerCode: '',
    sIDDraftOrder: '',
  },
  {
    request: getSingleDraftOrderRequested,
    resolve: getSingleDraftOrderResolved,
    reject: getSingleDraftOrderRejected,
  },
  data => {
    try {
      const { xmlst } = data;
      const { Success } = xmlst;

      return Success === 'TRUE';
    } catch (error) {
      return false;
    }
  },
);

export const getSingleDraftOrder = draftOrderID => async (
  dispatch,
  getState,
) => {
  const { users } = getState();
  const { currentUser } = users;
  const { customerID } = currentUser;

  return dispatch(
    getSingleDraftOrderEndpoint({
      sG5CustomerCode: customerID,
      sIDDraftOrder: draftOrderID,
    }),
  );
};

// ============================================================================
// processDraftOrder
// ============================================================================

export const sendCartAsDraftOrderRequested = fullParams => ({
  type: DRAFT_ORDER_SEND_CART_REQUESTED,
  params: fullParams,
});

export const sendCartAsDraftOrderResolved = (data, fullParams, dispatch) => {
  const { __options } = fullParams;
  const { __sIDDraftOrder, __hardRefresh } = __options;

  if (__sIDDraftOrder && __hardRefresh) {
    dispatch(getSingleDraftOrder(__sIDDraftOrder));
  }

  return {
    type: DRAFT_ORDER_SEND_CART_RESOLVED,
    payload: { data, params: fullParams },
  };
};

export const sendCartAsDraftOrderRejected = (error, fullParams) => ({
  type: DRAFT_ORDER_SEND_CART_REJECTED,
  payload: { error, params: fullParams },
});

export const sendCartAsDraftOrderEndpoint = createEndpoint(
  Type.POST,
  'processDraftOrder',
  {
    kIDUser: '',
    sDraftOrderJson: JSON.stringify({}),
  },
  {
    request: sendCartAsDraftOrderRequested,
    resolve: sendCartAsDraftOrderResolved,
    reject: sendCartAsDraftOrderRejected,
  },
  data => {
    try {
      const { xmlst } = data;
      const { Success } = xmlst;

      return Success === 'TRUE';
    } catch (error) {
      return false;
    }
  },
);

export const sendProductsAsDraftOrder = ({
  products,
  customerID,
  kIDUser,
  title,
  comment,
  draftOrderID = null,

  __hardRefresh = false,
}) => async dispatch => {
  const payload = {
    DraftOrder: {
      kIDDraftOrder: draftOrderID ?? -1,
      sContext: 'HX',
      sCustomerCode: customerID,
      sDraftOrderTitle: title || '',
      sDraftOrderComment: comment || '',
      items: {
        item: Object.values(products).map(
          ({ sCode, id, count, sFamille, sBelowComment }) => ({
            sArticleCode: sCode || id,
            sFamille: sFamille || '-1',
            sQuantite: count,
            sBelowComment: sBelowComment || '',
          }),
        ),
      },
    },
  };

  return dispatch(
    sendCartAsDraftOrderEndpoint({
      kIDUser,
      sDraftOrderJson: JSON.stringify(payload),

      __kIDPreOrder: draftOrderID ?? -1,
      __hardRefresh,
    }),
  );
};

export const sendCartAsDraftOrder = ({
  title,
  comment,
  draftOrderID = null,
}) => async (dispatch, getState) => {
  const { cart, users } = getState();

  const { currentUser } = users;
  const { customerID, id } = currentUser;

  const { present } = cart;
  const { products } = present;

  return dispatch(
    sendProductsAsDraftOrder({
      products,
      customerID,
      kIDUser: id,
      draftOrderID,
      title,
      comment,
    }),
  );
};

// ============================================================================
// manageDraftOrder
// ============================================================================

export const manageDraftOrderRequested = () => ({
  type: MANAGE_DRAFT_ORDER_REQUESTED,
});

export const manageDraftOrderResolved = nextStatus => ({
  type: MANAGE_DRAFT_ORDER_RESOLVED,
  payload: { nextStatus },
});

export const manageDraftOrderRejected = error => ({
  type: MANAGE_DRAFT_ORDER_REJECTED,
  payload: { error },
});

export const manageDraftOrderEndpoint = createEndpoint(
  Type.GET,
  'manageDraftOrder',
  {
    kIDUser: '',
    sG5CustomerCode: '',
    sIDDraftOrder: '',
    iAction: '',
  },
  {
    request: manageDraftOrderRequested,
    resolve: manageDraftOrderResolved,
    reject: manageDraftOrderRejected,
  },
  data => {
    try {
      const { xmlst } = data;
      const { Success } = xmlst;

      return Success === 'TRUE';
    } catch (error) {
      return false;
    }
  },
);

export const manageDraftOrder = (draftOrderId, nextStatus) => async (
  dispatch,
  getState,
) => {
  const { users } = getState();
  const { currentUser } = users;
  const { id, customerID } = currentUser;

  const response = await dispatch(
    manageDraftOrderEndpoint({
      kIDUser: id,
      sG5CustomerCode: customerID,
      sIDDraftOrder: draftOrderId,
      iAction: nextStatus,
    }),
  );

  await dispatch(getDraftOrders());

  return response;
};

// ============================================================================
// disableDraftOrder
// ============================================================================

export const disableDraftOrderRequested = () => ({
  type: DISABLE_DRAFT_ORDER_REQUESTED,
});

export const disableDraftOrderResolved = (data, fullParams) => ({
  type: DISABLE_DRAFT_ORDER_RESOLVED,
  payload: { data, fullParams },
});

export const disableDraftOrderRejected = error => ({
  type: DISABLE_DRAFT_ORDER_REJECTED,
  payload: { error },
});

export const disableDraftOrderEndpoint = createEndpoint(
  Type.GET,
  'disableDraftOrder',
  {
    kIDUser: '',
    sG5CustomerCode: '',
    sIDDraftOrder: '',
  },
  {
    request: disableDraftOrderRequested,
    resolve: disableDraftOrderResolved,
    reject: disableDraftOrderRejected,
  },
  data => {
    try {
      const { xmlst } = data;
      const { Success } = xmlst;

      return Success === 'TRUE';
    } catch (error) {
      return false;
    }
  },
);

export const disableDraftOrder = draftOrderID => async (dispatch, getState) => {
  const { users } = getState();
  const { currentUser } = users;
  const { id, customerID } = currentUser;

  const response = await dispatch(
    disableDraftOrderEndpoint({
      kIDUser: id,
      sG5CustomerCode: customerID,
      sIDDraftOrder: draftOrderID,
    }),
  );

  await dispatch(getSingleDraftOrder(draftOrderID));

  return response;
};

export const DRAFT_ORDER_ACTIONS = {
  getSingleDraftOrder,
  getDraftOrders,
  sendCartAsDraftOrder,
  manageDraftOrder,
  disableDraftOrder,
  getMultiAccountDraftOrderCounts,
};
